import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import EditChangeOrderModal from '@/components/feature/EditChangeOrderModal';
import NewChangeOrderModal from '@/components/feature/NewChangeOrderModal';
import { CHANGE_ORDERS_SYNC_EVENT, emitChangeOrdersSync } from '@/lib/changeOrderUtils';
import { formatInternalPricing } from '@/lib/changeOrderPricingDisplay';
import {
  inferChangeOrderPricingType,
  persistFormPricing,
} from '@/lib/changeOrderPricing';
import InstructionAttachmentThumbnails, { type InstructionAttachment } from '@/components/feature/InstructionAttachmentThumbnails';
import { formatDateTime } from '@/lib/dateUtils';
import {
  CO_INVOICE_FILE_TYPE,
  getInvoiceStatusLabel,
  getInvoiceStatusStyle,
} from '@/lib/changeOrderInvoice';
import { openProjectFileInNewTab, FILE_OPEN_ERROR_MESSAGE } from '@/lib/storageSignedUrl';

interface InvoiceFileRecord {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  storage_bucket: string | null;
  storage_path: string | null;
}

interface ChangeOrderRecord {
  id: string;
  project_id: string;
  instruction_id: string | null;
  title: string;
  description: string | null;
  client_description: string | null;
  reason: string | null;
  status: string;
  requires_client_approval: boolean;
  client_decision: string | null;
  client_comment: string | null;
  client_decided_at: string | null;
  cost_impact: string | null;
  schedule_impact: string | null;
  current_finish_description: string | null;
  new_finish_description: string | null;
  // New fields
  co_number: string | null;
  co_sequence: number | null;
  builder_cost: number | null;
  client_price: number | null;
  internal_note: string | null;
  client_visible: boolean | null;
  invoice_status: string | null;
  invoice_url: string | null;
  invoice_file_id: string | null;
  invoiced_at: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

interface Props {
  projectId: string;
}

// Unified statuses matching requirements
const CO_STATUSES = [
  'draft',
  'pending_client_approval',
  'approved',
  'rejected',
  'closed',
  // legacy values still supported:
  'sent_to_client',
  'client_approved',
  'client_rejected',
  'in_progress',
  'completed',
  'cancelled',
];

const CO_STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  draft:                    { bg: '#F3F4F6', text: '#6B7280' },
  pending_client_approval:  { bg: '#FEF0E7', text: '#E8621A' },
  sent_to_client:           { bg: '#FEF0E7', text: '#E8621A' },
  approved:                 { bg: '#F0FDF4', text: '#059669' },
  client_approved:          { bg: '#F0FDF4', text: '#059669' },
  rejected:                 { bg: '#FEF2F2', text: '#EF4444' },
  client_rejected:          { bg: '#FEF2F2', text: '#EF4444' },
  closed:                   { bg: '#F9FAFB', text: '#9CA3AF' },
  in_progress:              { bg: '#FFFBEB', text: '#F59E0B' },
  completed:                { bg: '#F0FDF4', text: '#059669' },
  cancelled:                { bg: '#F9FAFB', text: '#9CA3AF' },
};

function isLocked(status: string): boolean {
  return ['approved', 'client_approved', 'closed', 'completed', 'cancelled'].includes(status);
}

function isPending(status: string): boolean {
  return ['pending_client_approval', 'sent_to_client'].includes(status);
}

export default function ChangeOrdersTab({ projectId }: Props) {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [changeOrders, setChangeOrders] = useState<ChangeOrderRecord[]>([]);
  const [attachments, setAttachments] = useState<Record<string, InstructionAttachment[]>>();
  const [invoiceFiles, setInvoiceFiles] = useState<Record<string, InvoiceFileRecord>>({});
  const [invoiceFieldsAvailable, setInvoiceFieldsAvailable] = useState(true);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showNewCoModal, setShowNewCoModal] = useState(false);

  // Edit modal state
  const [editCo, setEditCo] = useState<ChangeOrderRecord | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Delete / Archive confirmation
  const [confirmCo, setConfirmCo] = useState<ChangeOrderRecord | null>(null);
  const [confirmType, setConfirmType] = useState<'delete' | 'archive'>('delete');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [fileOpenError, setFileOpenError] = useState('');

  // Create form state (inline, kept as-is)
  const emptyForm = {
    title: '',
    description: '',
    client_description: '',
    reason: '',
    current_finish_description: '',
    new_finish_description: '',
    schedule_impact: '',
    builder_cost: '',
    client_price: '',
    internal_note: '',
    status: 'draft',
    requires_client_approval: true,
    co_number: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [createError, setCreateError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);

    const selectWithInvoice =
      'id, project_id, instruction_id, title, description, client_description, reason, status, requires_client_approval, client_decision, client_comment, client_decided_at, cost_impact, schedule_impact, current_finish_description, new_finish_description, co_number, co_sequence, builder_cost, client_price, internal_note, client_visible, invoice_status, invoice_url, invoice_file_id, invoiced_at, paid_at, created_at, updated_at';
    const selectBase =
      'id, project_id, instruction_id, title, description, client_description, reason, status, requires_client_approval, client_decision, client_comment, client_decided_at, cost_impact, schedule_impact, current_finish_description, new_finish_description, co_number, co_sequence, builder_cost, client_price, internal_note, client_visible, created_at, updated_at';

    let coResult = await supabase
      .from('jphph_change_orders')
      .select(selectWithInvoice)
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .is('archived_at', null)
      .order('co_sequence', { ascending: true, nullsFirst: false })
      .order('co_number', { ascending: true })
      .order('created_at', { ascending: true });

    if (coResult.error && /invoice|schema cache|column/i.test(coResult.error.message)) {
      setInvoiceFieldsAvailable(false);
      coResult = (await supabase
        .from('jphph_change_orders')
        .select(selectBase)
        .eq('project_id', projectId)
        .eq('is_deleted', false)
        .is('archived_at', null)
        .order('co_sequence', { ascending: true, nullsFirst: false })
        .order('co_number', { ascending: true })
        .order('created_at', { ascending: true })) as typeof coResult;
    } else {
      setInvoiceFieldsAvailable(true);
    }

    const cos = (coResult.data || []) as ChangeOrderRecord[];

    // Deduplicate by id — protects against duplicate rows or join artifacts
    const uniqueCos = Array.from(new Map(cos.map((co) => [co.id, co])).values());
    setChangeOrders(uniqueCos);

    const invoiceFileIds = Array.from(
      new Set(uniqueCos.map((co) => co.invoice_file_id).filter((id): id is string => Boolean(id)))
    );

    if (invoiceFileIds.length > 0) {
      const { data: invoiceRows } = await supabase
        .from('jphph_project_files')
        .select('id, file_name, file_url, file_type, storage_bucket, storage_path')
        .in('id', invoiceFileIds);

      const invoiceMap: Record<string, InvoiceFileRecord> = {};
      (invoiceRows || []).forEach((row) => {
        const id = (row as { id: string }).id;
        invoiceMap[id] = {
          id,
          file_name: (row as { file_name: string }).file_name,
          file_url: (row as { file_url: string }).file_url,
          file_type: (row as { file_type: string | null }).file_type,
          storage_bucket: (row as { storage_bucket: string | null }).storage_bucket,
          storage_path: (row as { storage_path: string | null }).storage_path,
        };
      });
      setInvoiceFiles(invoiceMap);
    } else {
      setInvoiceFiles({});
    }

    if (cos.length > 0) {
      const coIds = cos.map((c) => c.id);
      const { data: filesData } = await supabase
        .from('jphph_project_files')
        .select('id, file_name, file_url, file_type, photo_type, caption, change_order_id, is_client_visible, is_subcontractor_visible, storage_bucket, storage_path')
        .or(coIds.map((id) => `change_order_id.eq.${id}`).join(','))
        .eq('is_deleted', false);

      const grouped: Record<string, InstructionAttachment[]> = {};
      (filesData || []).forEach((f) => {
        if ((f as Record<string, unknown>).photo_type === CO_INVOICE_FILE_TYPE) return;
        const key = (f as Record<string, unknown>).change_order_id as string || '';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push({
          id: (f as Record<string, unknown>).id as string,
          file_url: (f as Record<string, unknown>).file_url as string,
          file_name: (f as Record<string, unknown>).file_name as string,
          file_type: (f as Record<string, unknown>).file_type as string | null,
          photo_type: (f as Record<string, unknown>).photo_type as string | null,
          caption: (f as Record<string, unknown>).caption as string | null,
          is_client_visible: (f as Record<string, unknown>).is_client_visible as boolean | null,
          is_subcontractor_visible: (f as Record<string, unknown>).is_subcontractor_visible as boolean | null,
          storage_bucket: (f as Record<string, unknown>).storage_bucket as string | null,
          storage_path: (f as Record<string, unknown>).storage_path as string | null,
        });
      });
      setAttachments(grouped);
    } else {
      setAttachments({});
    }

    setLoading(false);
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      if (!detail?.projectId || detail.projectId === projectId) {
        void load();
      }
    };
    window.addEventListener(CHANGE_ORDERS_SYNC_EVENT, handler);
    return () => window.removeEventListener(CHANGE_ORDERS_SYNC_EVENT, handler);
  }, [load, projectId]);

  // Auto-suggest CO number when create form opens
  useEffect(() => {
    if (!showCreate || !projectId || form.co_number) return;
    let cancelled = false;
    getNextCoSequence().then(({ number }) => {
      if (!cancelled && !form.co_number) {
        setForm((prev) => ({ ...prev, co_number: number }));
      }
    });
    return () => { cancelled = true; };
  }, [showCreate, projectId]);

  function getStatusLabel(status: string): string {
    const map: Record<string, string> = {
      draft: t('co.status.draft'),
      pending_client_approval: t('co.status.pendingApproval'),
      sent_to_client: t('co.status.pendingApproval'),
      approved: t('co.status.approved'),
      client_approved: t('co.status.approved'),
      rejected: t('co.status.rejected'),
      client_rejected: t('co.status.rejected'),
      closed: t('co.status.closed'),
      in_progress: t('co.status.inProgress'),
      completed: t('co.status.completed'),
      cancelled: t('co.status.cancelled'),
    };
    return map[status] || status;
  }

  const filtered = changeOrders.filter((co) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === 'pending_client') return isPending(co.status);
    if (statusFilter === 'approved') return co.status === 'approved' || co.status === 'client_approved';
    if (statusFilter === 'rejected') return co.status === 'rejected' || co.status === 'client_rejected';
    return co.status === statusFilter;
  });

  const stats = {
    total: changeOrders.length,
    pendingClient: changeOrders.filter((c) => isPending(c.status)).length,
    approved: changeOrders.filter((c) => c.status === 'approved' || c.status === 'client_approved').length,
    draft: changeOrders.filter((c) => c.status === 'draft').length,
  };

  async function updateStatus(id: string, newStatus: string) {
    await supabase.from('jphph_change_orders').update({ status: newStatus, updated_at: new Date().toISOString() }).eq('id', id);
    setChangeOrders((prev) => prev.map((c) => c.id === id ? { ...c, status: newStatus } : c));
  }

  async function handleDeleteOrArchive(co: ChangeOrderRecord, type: 'delete' | 'archive') {
    setDeleteLoading(true);
    setDeleteError('');
    const now = new Date().toISOString();

    if (type === 'delete') {
      const { error } = await supabase
        .from('jphph_change_orders')
        .update({ is_deleted: true, deleted_at: now, deleted_by: user?.id || null })
        .eq('id', co.id);
      if (error) {
        setDeleteError(error.message);
        setDeleteLoading(false);
        return;
      }
    } else {
      const { error } = await supabase
        .from('jphph_change_orders')
        .update({ archived_at: now })
        .eq('id', co.id);
      if (error) {
        setDeleteError(error.message);
        setDeleteLoading(false);
        return;
      }
    }

    setChangeOrders((prev) => prev.filter((c) => c.id !== co.id));
    setConfirmCo(null);
    setDeleteLoading(false);
  }

  function openEditModal(co: ChangeOrderRecord) {
    setEditCo(co);
    setShowEditModal(true);
  }

  function openCreate() {
    setShowNewCoModal(true);
  }

  async function getNextCoSequence(): Promise<{ seq: number; number: string }> {
    const { data } = await supabase
      .from('jphph_change_orders')
      .select('co_sequence')
      .eq('project_id', projectId)
      .order('co_sequence', { ascending: false })
      .limit(1);

    const maxSeq = (data && data.length > 0 && data[0].co_sequence != null) ? data[0].co_sequence : 0;
    const seq = maxSeq + 1;
    return { seq, number: `CO-${String(seq).padStart(3, '0')}` };
  }

  async function handleSaveCreate() {
    if (!form.title.trim()) return;
    setSaving(true);
    setCreateError('');
    try {
      // Validate duplicate CO number
      if (form.co_number.trim()) {
        const { data: dupCheck } = await supabase
          .from('jphph_change_orders')
          .select('id')
          .eq('project_id', projectId)
          .eq('co_number', form.co_number.trim())
          .eq('is_deleted', false);
        if (dupCheck && dupCheck.length > 0) {
          setCreateError('This CO number already exists for this project.');
          setSaving(false);
          return;
        }
      }

      const builderNum = form.builder_cost.trim() ? Number(form.builder_cost) : null;
      const clientNum = form.client_price.trim() ? Number(form.client_price) : null;
      const pricingType = inferChangeOrderPricingType(
        Number.isFinite(builderNum as number) ? builderNum : null,
        Number.isFinite(clientNum as number) ? clientNum : null,
        { title: form.title, reason: form.reason }
      );
      const pricingResult = persistFormPricing(
        builderNum != null && Number.isFinite(builderNum) ? String(Math.abs(builderNum)) : form.builder_cost,
        clientNum != null && Number.isFinite(clientNum) ? String(Math.abs(clientNum)) : form.client_price,
        pricingType
      );
      if (pricingResult.error) {
        setCreateError(pricingResult.error);
        setSaving(false);
        return;
      }

      const { seq } = await getNextCoSequence();
      const payload: Record<string, unknown> = {
        project_id: projectId,
        title: form.title.trim(),
        description: form.description.trim() || null,
        client_description: form.client_description.trim() || null,
        reason: form.reason.trim() || null,
        current_finish_description: form.current_finish_description.trim() || null,
        new_finish_description: form.new_finish_description.trim() || null,
        schedule_impact: form.schedule_impact.trim() || null,
        builder_cost: pricingResult.pricing.builder_cost,
        client_price: pricingResult.pricing.client_price,
        internal_note: form.internal_note.trim() || null,
        status: form.status,
        requires_client_approval: form.requires_client_approval,
        co_sequence: seq,
        co_number: form.co_number.trim() || null,
        created_by: user?.id || null,
      };

      const { error: insertErr } = await supabase.from('jphph_change_orders').insert(payload);
      if (insertErr) {
        setCreateError(insertErr.message);
        setSaving(false);
        return;
      }

      await load();
      setShowCreate(false);
      setForm(emptyForm);
    } finally {
      setSaving(false);
    }
  }

  function cancelCreate() {
    setShowCreate(false);
    setForm(emptyForm);
    setCreateError('');
  }

  return (
    <div className="space-y-4">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {[
          { label: t('co.stats.total'), value: stats.total, color: '#374151', bg: '#F9FAFB' },
          { label: t('co.stats.pendingClient'), value: stats.pendingClient, color: '#E8621A', bg: '#FEF0E7' },
          { label: t('co.stats.approved'), value: stats.approved, color: '#059669', bg: '#F0FDF4' },
          { label: t('co.stats.draft'), value: stats.draft, color: '#9CA3AF', bg: '#F3F4F6' },
        ].map((s) => (
          <div key={s.label} className="rounded-2xl border border-gray-100 p-3 text-center" style={{ backgroundColor: s.bg }}>
            <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
            <div className="text-xs mt-0.5" style={{ color: s.color }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter + create row */}
      <div className="flex flex-col gap-2">
        <div className="w-full overflow-x-auto scrollbar-hide">
          <div className="flex flex-nowrap gap-1.5 min-w-max">
            {[
              { key: 'all', label: t('co.filter.all') },
              { key: 'pending_client', label: t('co.filter.pendingClient') },
              { key: 'approved', label: t('co.filter.approved') },
              { key: 'rejected', label: t('co.filter.rejected') },
              { key: 'draft', label: t('co.filter.draft') },
              { key: 'closed', label: t('co.filter.closed') },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setStatusFilter(f.key)}
                className={`flex-shrink-0 px-3 py-2 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors min-h-[36px] ${
                  statusFilter === f.key ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex justify-end">
          <button
            onClick={openCreate}
            className="flex items-center gap-1.5 bg-[#E8621A] text-white px-3 py-2 rounded-xl text-xs font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            {t('co.create')}
          </button>
        </div>
      </div>

      {/* Pending client approval alert */}
      {stats.pendingClient > 0 && (
        <div className="bg-[#FEF0E7] border border-[#FDBA74] rounded-2xl p-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-[#E8621A] flex items-center justify-center flex-shrink-0">
            <i className="ri-time-line text-white text-sm"></i>
          </div>
          <div className="flex-1">
            <p className="text-[#E8621A] font-bold text-sm">{stats.pendingClient} {t('co.alert.pendingClientApproval')}</p>
            <p className="text-[#374151] text-xs mt-0.5">{t('co.alert.pendingClientDesc')}</p>
          </div>
        </div>
      )}

      {/* Create Form (inline only) */}
      {showCreate && (
        <div className="bg-white rounded-2xl border border-[#E8621A]/30 p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-[#1A1A1A]">{t('co.form.createTitle')}</h3>
            <button onClick={cancelCreate} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer">
              <i className="ri-close-line text-[#6B7280]"></i>
            </button>
          </div>

          {createError && (
            <div className="bg-[#FEF2F2] rounded-xl p-3 border border-[#FECACA] flex items-start gap-2">
              <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-[#EF4444] text-xs">{createError}</p>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* CO Number */}
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#374151] mb-1 block">CO Number *</label>
              <input
                value={form.co_number}
                onChange={(e) => setForm((p) => ({ ...p, co_number: e.target.value }))}
                placeholder="e.g. CO-001"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>

            {/* Title */}
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#374151] mb-1 block">{t('co.form.titleLabel')} *</label>
              <input
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                placeholder={t('co.form.titlePlaceholder')}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>

            {/* Change description (internal) */}
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#374151] mb-1 block">
                {t('co.form.descriptionLabel')}
                <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">({t('co.form.internalOnly')})</span>
              </label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder={t('co.form.descriptionPlaceholder')}
                rows={2}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
              />
            </div>

            {/* Client description (client-visible) */}
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#374151] mb-1 block">
                {t('co.form.clientDescLabel')}
                <span className="ml-1 text-[10px] font-normal text-[#059669]">({t('co.form.clientVisible')})</span>
              </label>
              <textarea
                value={form.client_description}
                onChange={(e) => setForm((p) => ({ ...p, client_description: e.target.value }))}
                placeholder={t('co.form.clientDescPlaceholder')}
                rows={2}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
              />
            </div>

            {/* Reason */}
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#374151] mb-1 block">{t('co.form.reasonLabel')}</label>
              <input
                value={form.reason}
                onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                placeholder={t('co.form.reasonPlaceholder')}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>

            {/* Current Finish */}
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">{t('co.form.currentFinishLabel')}</label>
              <input
                value={form.current_finish_description}
                onChange={(e) => setForm((p) => ({ ...p, current_finish_description: e.target.value }))}
                placeholder={t('co.form.currentFinishPlaceholder')}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>

            {/* New Finish */}
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">{t('co.form.newFinishLabel')}</label>
              <input
                value={form.new_finish_description}
                onChange={(e) => setForm((p) => ({ ...p, new_finish_description: e.target.value }))}
                placeholder={t('co.form.newFinishPlaceholder')}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>

            {/* Schedule Impact */}
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#374151] mb-1 block">{t('co.form.scheduleImpactLabel')}</label>
              <input
                value={form.schedule_impact}
                onChange={(e) => setForm((p) => ({ ...p, schedule_impact: e.target.value }))}
                placeholder={t('co.form.scheduleImpactPlaceholder')}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>

            {/* Builder Cost (internal) */}
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">
                Builder Cost (excl. GST)
                <span className="ml-1 text-[10px] font-normal text-[#EF4444]">({t('co.form.internalOnly')})</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={form.builder_cost}
                onChange={(e) => setForm((p) => ({ ...p, builder_cost: e.target.value }))}
                placeholder="0.00"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>

            {/* Client Price (client-visible) */}
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">
                Client Price (incl. GST)
                <span className="ml-1 text-[10px] font-normal text-[#059669]">({t('co.form.clientVisible')})</span>
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={form.client_price}
                onChange={(e) => setForm((p) => ({ ...p, client_price: e.target.value }))}
                placeholder="0.00"
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors"
              />
            </div>

            {/* Internal Note */}
            <div className="sm:col-span-2">
              <label className="text-xs font-semibold text-[#374151] mb-1 block">
                {t('co.form.internalNoteLabel')}
                <span className="ml-1 text-[10px] font-normal text-[#9CA3AF]">({t('co.form.internalOnly')})</span>
              </label>
              <textarea
                value={form.internal_note}
                onChange={(e) => setForm((p) => ({ ...p, internal_note: e.target.value }))}
                placeholder={t('co.form.internalNotePlaceholder')}
                rows={2}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors resize-none"
              />
            </div>

            {/* Requires Client Approval */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="requires_client_approval"
                checked={form.requires_client_approval}
                onChange={(e) => setForm((p) => ({ ...p, requires_client_approval: e.target.checked }))}
                className="w-4 h-4 accent-[#E8621A] cursor-pointer"
              />
              <label htmlFor="requires_client_approval" className="text-xs text-[#374151] cursor-pointer">
                {t('co.form.requiresClientApproval')}
              </label>
            </div>
          </div>

          {/* Save / Cancel */}
          <div className="flex gap-2 justify-end pt-1">
            <button
              onClick={cancelCreate}
              className="px-4 py-2 rounded-xl border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer whitespace-nowrap"
            >
              {t('btn.cancel')}
            </button>
            <button
              onClick={handleSaveCreate}
              disabled={saving || !form.title.trim()}
              className="px-4 py-2 rounded-xl bg-[#E8621A] text-white text-xs font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : t('co.form.create')}
            </button>
          </div>
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-16">
          <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl"></i>
        </div>
      )}

      {fileOpenError && (
        <div className="mb-3 bg-[#FEF2F2] border border-[#FECACA] rounded-xl px-3 py-2.5 text-[#EF4444] text-xs">
          {fileOpenError}
        </div>
      )}

      {!loading && filtered.length === 0 && !showCreate && (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
          <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
            <i className="ri-exchange-line text-5xl"></i>
          </div>
          <p className="text-[#374151] font-semibold mb-1">{t('co.empty.title')}</p>
          <p className="text-[#9CA3AF] text-sm mb-4">{t('co.empty.desc')}</p>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-1.5 bg-[#E8621A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line"></i>{t('co.create')}
          </button>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map((co) => {
            const isExpanded = expandedId === co.id;
            const statusStyle = CO_STATUS_STYLES[co.status] || CO_STATUS_STYLES.draft;
            const coAttachments = attachments[co.id] || [];
            const invoiceFile = co.invoice_file_id ? invoiceFiles[co.invoice_file_id] : undefined;
            const invoiceStyle = getInvoiceStatusStyle(co.invoice_status);
            const isPendingApproval = co.requires_client_approval && isPending(co.status);
            const locked = isLocked(co.status);
            const pricing = formatInternalPricing(co.builder_cost, co.client_price, {
              title: co.title,
              reason: co.reason,
            });

            return (
              <div
                key={co.id}
                className={`bg-white rounded-2xl border overflow-hidden ${isPendingApproval ? 'border-[#E8621A]/30' : 'border-gray-100'}`}
              >
                {isPendingApproval && <div className="h-1 bg-[#E8621A]"></div>}
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        {/* CO Number badge */}
                        {co.co_number && (
                          <span className="text-xs font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-lg tracking-wide flex-shrink-0">
                            {co.co_number}
                          </span>
                        )}
                        <h4 className="text-sm font-semibold text-[#1A1A1A] leading-snug">{co.title}</h4>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                        style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                      >
                        {getStatusLabel(co.status)}
                      </span>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                        style={{ backgroundColor: invoiceStyle.bg, color: invoiceStyle.text }}
                      >
                        {getInvoiceStatusLabel(co.invoice_status)}
                      </span>
                      <button
                        onClick={() => openEditModal(co)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer flex-shrink-0"
                        title={t('co.action.edit')}
                      >
                        <i className="ri-edit-line text-[#9CA3AF] text-sm"></i>
                      </button>
                      {locked && (
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" title={t('co.lockedTooltip')}>
                          <i className="ri-lock-line text-[#D1D5DB] text-sm"></i>
                        </div>
                      )}
                      {/* Delete / Archive */}
                      {locked ? (
                        <button
                          onClick={() => { setConfirmCo(co); setConfirmType('archive'); setDeleteError(''); }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#F5F3FF] cursor-pointer flex-shrink-0"
                          title="Archive"
                        >
                          <i className="ri-archive-line text-[#7C3AED] text-sm"></i>
                        </button>
                      ) : (
                        <button
                          onClick={() => { setConfirmCo(co); setConfirmType('delete'); setDeleteError(''); }}
                          className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#FEF2F2] cursor-pointer flex-shrink-0"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-[#EF4444] text-sm"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {co.requires_client_approval && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A]">
                        <i className="ri-user-line mr-0.5"></i>{t('co.badge.clientApproval')}
                      </span>
                    )}
                    {co.client_decision === 'approved' && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#F0FDF4] text-[#059669]">
                        <i className="ri-check-line mr-0.5"></i>{t('co.badge.clientApproved')}
                      </span>
                    )}
                    {co.client_decision === 'declined' && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FEF2F2] text-[#EF4444]">
                        <i className="ri-close-line mr-0.5"></i>{t('co.badge.clientDeclined')}
                      </span>
                    )}
                    <span className="text-[#9CA3AF] text-xs ml-auto">{formatDateTime(co.created_at)}</span>
                  </div>

                  {/* Change Order Details */}
                  <div className="bg-[#F5F3FF]/50 rounded-xl px-3 py-2.5 border border-[#7C3AED]/10 mb-2">
                    <p className="text-[10px] font-bold text-[#7C3AED] uppercase tracking-wider mb-1.5">{t('co.details.title')}</p>
                    <div className="space-y-1">
                      {co.reason && (
                        <p className="text-xs text-[#374151]">
                          <span className="font-semibold">{t('co.details.reason')}</span> {co.reason}
                        </p>
                      )}
                      {co.current_finish_description && (
                        <p className="text-xs text-[#374151]">
                          <span className="font-semibold">{t('co.details.currentFinish')}</span> {co.current_finish_description}
                        </p>
                      )}
                      {co.new_finish_description && (
                        <p className="text-xs text-[#374151]">
                          <span className="font-semibold">{t('co.details.newFinish')}</span> {co.new_finish_description}
                        </p>
                      )}
                      <div className="flex gap-4 flex-wrap">
                        {co.schedule_impact && (
                          <p className="text-xs text-[#374151]">
                            <span className="font-semibold">{t('co.details.scheduleImpact')}</span> {co.schedule_impact}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Invoice link — internal only */}
                  {(co.invoice_url || co.invoice_file_id || (co.invoice_status && co.invoice_status !== 'not_invoiced')) && (
                    <div className="bg-[#F9FAFB] rounded-xl px-3 py-2.5 border border-gray-100 mb-2">
                      <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1.5">
                        <i className="ri-file-list-3-line mr-1" />
                        Invoice (internal)
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: invoiceStyle.bg, color: invoiceStyle.text }}
                        >
                          {getInvoiceStatusLabel(co.invoice_status)}
                        </span>
                        {co.invoice_url && (
                          <a
                            href={co.invoice_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-[#E8621A] font-semibold hover:underline inline-flex items-center gap-1"
                          >
                            <i className="ri-external-link-line" />
                            Open invoice URL
                          </a>
                        )}
                        {invoiceFile && (
                          <button
                            type="button"
                            onClick={() => {
                              void (async () => {
                                const result = await openProjectFileInNewTab(invoiceFile);
                                if (!result.ok) {
                                  setFileOpenError(result.error || FILE_OPEN_ERROR_MESSAGE);
                                } else {
                                  setFileOpenError('');
                                }
                              })();
                            }}
                            className="text-xs text-[#E8621A] font-semibold hover:underline inline-flex items-center gap-1 cursor-pointer"
                          >
                            <i className="ri-file-text-line" />
                            View invoice · {invoiceFile.file_name}
                          </button>
                        )}
                        {co.invoice_file_id && !invoiceFile && (
                          <span className="text-xs text-[#6B7280]">Invoice file linked (loading…)</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* INTERNAL COST SECTION — management only */}
                  {(co.builder_cost != null || co.client_price != null || co.internal_note) && (
                    <div className="bg-[#FEF9C3]/50 rounded-xl px-3 py-2.5 border border-[#F59E0B]/20 mb-2">
                      <p className="text-[10px] font-bold text-[#92400E] uppercase tracking-wider mb-1.5">
                        <i className="ri-eye-off-line mr-1"></i>{t('co.internal.title')}
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {co.builder_cost != null && (
                          <div>
                            <p className="text-[10px] text-[#9CA3AF]">{pricing.builderLabel}</p>
                            <p className="text-sm font-bold text-[#1A1A1A]">{pricing.builderText}</p>
                          </div>
                        )}
                        {co.client_price != null && (
                          <div>
                            <p className="text-[10px] text-[#9CA3AF]">{pricing.clientLabel}</p>
                            <p className={`text-sm font-bold ${pricing.isCredit ? 'text-[#7C3AED]' : 'text-[#1A1A1A]'}`}>{pricing.clientText}</p>
                          </div>
                        )}
                      </div>
                      {co.internal_note && (
                        <p className="text-xs text-[#6B7280] mt-2 leading-relaxed italic">{co.internal_note}</p>
                      )}
                    </div>
                  )}

                  {/* Client-visible description */}
                  {co.client_description && (
                    <div className="mb-2">
                      <p className="text-[10px] font-bold text-[#059669] uppercase tracking-wider mb-1">
                        <i className="ri-eye-line mr-1"></i>{t('co.clientDesc.label')}
                      </p>
                      <p className={`text-[#6B7280] text-sm leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                        {co.client_description}
                      </p>
                      {co.client_description.length > 120 && (
                        <button onClick={() => setExpandedId(isExpanded ? null : co.id)} className="text-[#059669] text-xs font-semibold cursor-pointer mt-1 whitespace-nowrap">
                          {isExpanded ? t('co.showLess') : t('co.readMore')}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Internal description (collapsible) */}
                  {co.description && !co.client_description && (
                    <p className={`text-[#6B7280] text-sm leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                      {co.description}
                    </p>
                  )}

                  {/* Client comment */}
                  {co.client_comment && (
                    <div className="mt-2 bg-[#F9FAFB] rounded-xl px-3 py-2 border border-gray-100">
                      <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-0.5">{t('co.clientComment')}</p>
                      <p className="text-xs text-[#374151] italic">{co.client_comment}</p>
                    </div>
                  )}

                  {/* Attachments */}
                  {coAttachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-50">
                      <InstructionAttachmentThumbnails files={coAttachments} maxVisible={3} />
                    </div>
                  )}

                  {/* Locked indicator */}
                  {locked && (
                    <div className="mt-3 pt-2 border-t border-gray-50 flex items-center gap-1.5">
                      <i className="ri-lock-line text-[#9CA3AF] text-xs"></i>
                      <span className="text-[#9CA3AF] text-xs">{t('co.lockedMessage')}</span>
                    </div>
                  )}

                  {/* Actions — only for non-locked */}
                  {!locked && (
                    <div className="mt-3 pt-3 border-t border-gray-50 flex items-center gap-2 flex-wrap">
                      {co.status === 'draft' && (
                        <button
                          onClick={() => updateStatus(co.id, 'pending_client_approval')}
                          className="px-3 py-1.5 rounded-lg bg-[#FEF0E7] text-[#E8621A] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#E8621A]/10 transition-colors"
                        >
                          <i className="ri-send-plane-line mr-0.5"></i>{t('co.action.sendToClient')}
                        </button>
                      )}
                      {isPending(co.status) && (
                        <>
                          <button
                            onClick={() => updateStatus(co.id, 'approved')}
                            className="px-3 py-1.5 rounded-lg bg-[#F0FDF4] text-[#059669] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#059669]/10 transition-colors"
                          >
                            <i className="ri-check-line mr-0.5"></i>{t('co.action.markApproved')}
                          </button>
                          <button
                            onClick={() => updateStatus(co.id, 'rejected')}
                            className="px-3 py-1.5 rounded-lg bg-[#FEF2F2] text-[#EF4444] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-[#EF4444]/10 transition-colors"
                          >
                            <i className="ri-close-line mr-0.5"></i>{t('co.action.markRejected')}
                          </button>
                        </>
                      )}
                      {(co.status === 'approved' || co.status === 'client_approved' || co.status === 'in_progress') && (
                        <button
                          onClick={() => updateStatus(co.id, 'closed')}
                          className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#6B7280] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-gray-200 transition-colors"
                        >
                          <i className="ri-close-circle-line mr-0.5"></i>{t('co.action.close')}
                        </button>
                      )}
                      {co.status === 'rejected' && (
                        <button
                          onClick={() => updateStatus(co.id, 'draft')}
                          className="px-3 py-1.5 rounded-lg bg-gray-100 text-[#374151] text-xs font-bold cursor-pointer whitespace-nowrap hover:bg-gray-200 transition-colors"
                        >
                          <i className="ri-arrow-go-back-line mr-0.5"></i>{t('co.action.revertToDraft')}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Modal */}
      {showNewCoModal && (
        <NewChangeOrderModal
          show={showNewCoModal}
          onClose={() => setShowNewCoModal(false)}
          projectId={projectId}
          allowProjectChange={false}
          source="project-detail-change-orders"
          onSuccess={() => {
            setShowNewCoModal(false);
            emitChangeOrdersSync(projectId);
            void load();
          }}
        />
      )}

      {showEditModal && editCo && (
        <EditChangeOrderModal
          show={showEditModal}
          co={editCo}
          attachments={attachments[editCo.id] || []}
          invoiceFieldsAvailable={invoiceFieldsAvailable}
          onClose={() => { setShowEditModal(false); setEditCo(null); }}
          onSaved={() => { emitChangeOrdersSync(projectId); void load(); setShowEditModal(false); setEditCo(null); }}
        />
      )}

      {/* Delete / Archive Confirmation Modal */}
      {confirmCo && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => { setConfirmCo(null); setDeleteError(''); }}></div>
          <div className="relative bg-white w-full max-w-sm mx-4 rounded-2xl p-5 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${confirmType === 'delete' ? 'bg-[#FEF2F2]' : 'bg-[#F5F3FF]'}`}>
                <i className={`text-sm ${confirmType === 'delete' ? 'ri-delete-bin-line text-[#EF4444]' : 'ri-archive-line text-[#7C3AED]'}`}></i>
              </div>
              <h3 className="text-sm font-bold text-[#1A1A1A]">
                {confirmType === 'delete' ? 'Delete Change Order?' : 'Archive Change Order?'}
              </h3>
            </div>
            <p className="text-xs text-[#6B7280] leading-relaxed mb-4">
              {confirmType === 'delete'
                ? 'This Change Order will be removed from the active list. This action should only be used for mistakes or duplicate entries.'
                : 'Approved or closed Change Orders are part of the project record. They cannot be permanently deleted. You can archive them from the active list.'}
            </p>
            {confirmCo.co_number && (
              <p className="text-xs text-[#9CA3AF] mb-4">
                <span className="font-semibold text-[#E8621A]">{confirmCo.co_number}</span> · {confirmCo.title}
              </p>
            )}
            {deleteError && (
              <div className="bg-[#FEF2F2] rounded-xl p-3 border border-[#FECACA] flex items-start gap-2 mb-4">
                <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5"></i>
                <p className="text-[#EF4444] text-xs">{deleteError}</p>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={() => { setConfirmCo(null); setDeleteError(''); }}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-[#374151] text-xs font-semibold cursor-pointer whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteOrArchive(confirmCo, confirmType)}
                disabled={deleteLoading}
                className={`flex-1 py-2.5 rounded-xl text-white text-xs font-bold cursor-pointer whitespace-nowrap disabled:opacity-50 ${
                  confirmType === 'delete' ? 'bg-[#EF4444]' : 'bg-[#7C3AED]'
                }`}
              >
                {deleteLoading ? (
                  <i className="ri-loader-4-line animate-spin"></i>
                ) : confirmType === 'delete' ? (
                  'Delete'
                ) : (
                  'Archive'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}